// Reusable deep-link and share helpers for events
// Exported helpers: slugify, extractStartDateFromRange, extractLocationForUrl,
// buildDeepLink, isSharedScheduleMatch, shareDeepLink

const slugify = (text = '') => {
  if (!text) return '';
  return String(text)
    .normalize('NFC') // Normalize Unicode to composed form (preserves Devanagari syllables)
    .toLowerCase()
    .trim()
    // Replace punctuation and special chars with spaces, preserve Unicode letters/numbers
    .replace(/[^\p{L}\p{M}\p{N}\s-]/gu, ' ') // \p{M} includes combining marks for Devanagari
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Collapse multiple hyphens
    .replace(/^-+|-+$/g, ''); // Trim hyphens from start/end
};

const extractStartDateFromRange = (input) => {
  if (!input) return '';

  // Handle Date objects explicitly - convert to ISO format (YYYY-MM-DD)
  if (input instanceof Date) {
    return input.toISOString().split('T')[0];
  }

  // If input is an object with startDate/start_date/startDateOriginal
  if (typeof input === 'object') {
    const keys = ['startDate', 'start_date', 'startDateOriginal', 'start_date_format'];
    for (const k of keys) {
      const val = input[k];
      // If value is a Date object, convert to ISO
      if (val instanceof Date) {
        return val.toISOString().split('T')[0];
      }
      if (val) return slugify(String(val));
    }
  }

  const str = String(input);

  // Check if it's already in ISO format (YYYY-MM-DD) - return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(str.trim())) {
    return str.trim();
  }

  // Try to split ranges like "Fri Feb 13 2026 – Sun Feb 15 2026" or "13-02-2026 - 15-02-2026"
  const sep = str.includes('–') ? '–' : (str.includes('-') && /\d\s*-\s*\d|\d{4}-\d{2}-\d{2}/.test(str) ? '-' : null);
  let candidate = str;
  if (sep) {
    const parts = str.split(sep);
    candidate = parts[0] || str;
  } else if (str.includes(' to ')) {
    candidate = str.split(' to ')[0];
  }

  // Match common date patterns
  const patterns = [/(\d{4}-\d{2}-\d{2})/, /(\d{1,2}\s+\w+\s+\d{4})/, /(\w+\s+\d{1,2}\s+\d{4})/];
  for (const p of patterns) {
    const m = candidate.match(p);
    if (m && m[1]) return slugify(m[1]);
  }

  // Fallback: use first 3 words
  return slugify(candidate.split(/[\s,]+/).slice(0, 3).join(' '));
};

const extractLocationForUrl = (location) => {
  if (!location) return '';
  if (typeof location === 'object') {
    // prefer city_name/venue_address or location fields
    const keys = ['city_name', 'venue_address', 'location', 'cityName', 'venueAddress', 'fullAddress'];
    for (const k of keys) {
      if (location[k]) return slugify(String(location[k]));
    }
  }
  return slugify(String(location));
};

const buildDeepLink = ({ origin = '', eventId, title = '', schedule_id = null, dateForUrl = '', locationForUrl = '', intent = '' }) => {
  // Build a deep link using minimal required parameters only (event_id, schedule_id, and optional intent)
  // As per requirements: URLs should primarily contain IDs only
  // Example: /event_details?event_id=7&schedule_id=3039
  // Or with intent: /event_details/19?schedule_id=3116&intent=checkout
  const params = new URLSearchParams();
  
  if (eventId !== undefined && eventId !== null) params.set('event_id', String(eventId));
  if (schedule_id !== undefined && schedule_id !== null) params.set('schedule_id', String(schedule_id));
  if (intent) params.set('intent', intent); // Optional intent parameter (e.g., 'checkout')
  
  const base = origin.replace(/\/$/, '') + '/event_details';
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

const isSharedScheduleMatch = ({ eventId, dateRange, location, schedule_id }) => {
  if (typeof window === 'undefined') return false;
  const url = window.location.href || '';
  try {
    const u = new URL(url);

    // Prefer query params (GET) as the primary source of truth
    const params = u.searchParams;
    if (params.get('event_id') && String(params.get('event_id')) === String(eventId)) {
      // If schedule_id is provided in the URL, require exact match
      if (params.get('schedule_id')) {
        return schedule_id && String(params.get('schedule_id')) === String(schedule_id);
      }

      // BACKWARD COMPATIBILITY: If date and/or location are provided, require at least one to match
      // This is kept for legacy URLs that still use date/location parameters
      const dateSlug = extractStartDateFromRange(dateRange);
      const locSlug = extractLocationForUrl(location);
      const dateMatch = dateSlug && params.get('date') && params.get('date').includes(dateSlug);
      const locMatch = locSlug && params.get('location') && params.get('location').includes(locSlug);
      if (dateMatch || locMatch) return true;

      // No specific schedule identifier (schedule_id, date, or location) in URL params
      // Do not mark any schedule as "selected" - the user shared the event, not a specific schedule
      return false;
    }

    // Fallback: keep existing path-based checks for backward compatibility
    const path = u.pathname || '';
    if (schedule_id && path.includes(`/event_details/${eventId}/`) && path.includes(`/schedule/${schedule_id}/`)) return true;
    const dateSlug = extractStartDateFromRange(dateRange);
    const locSlug = extractLocationForUrl(location);
    if (dateSlug && locSlug && path.includes(`/event_details/${eventId}/`) && path.includes(`/${dateSlug}/`) && path.includes(`/${locSlug}`)) return true;
  } catch (e) {
    // ignore
  }
  return false;
};

const shareDeepLink = async ({ title = '', text = '', url = '' }) => {
  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      // Only include url field if it's non-empty to avoid duplicate links
      const shareData = { title, text };
      if (url) shareData.url = url;
      await navigator.share(shareData);
      return true;
    } catch (err) {
      // user cancelled or failed, fallback
    }
  }

  // Fallback: copy to clipboard (prefer full text if url is empty)
  const clipboardContent = url || text;
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(clipboardContent);
      alert('Link copied to clipboard');
      return true;
    }
  } catch (e) {
    // final fallback: prompt
    try { window.prompt('Copy this link', clipboardContent); } catch (e) {}
  }
  return false;
};

// Detect if URL contains Marathi (Devanagari) characters
const detectMarathiInUrl = (url = '') => {
  if (!url) return false;
  try {
    // Decode URI components to get actual Unicode characters
    const decodedUrl = decodeURIComponent(url);
    // Check for Devanagari Unicode range (U+0900 to U+097F)
    return /[\u0900-\u097F]/.test(decodedUrl);
  } catch (e) {
    // If decoding fails, check original URL
    return /[\u0900-\u097F]/.test(url);
  }
};

export { slugify, extractStartDateFromRange, extractLocationForUrl, buildDeepLink, isSharedScheduleMatch, shareDeepLink, detectMarathiInUrl };
